import { AxiosError, AxiosResponse } from 'axios';
import qs from 'qs-stringify';

import { RESPONSE_CODES } from '../constants/responseCodes.constants';
import { PaginationFactory, PaginatorFactory } from '../factory';
import { api } from '../http';
import { store } from '../store';
import { DefaultRes, UserTypes } from '../types';

export class UserService {
    public static readonly dispatch = store.dispatch.user;
    public static readonly state = store.getState();

    public static async fetchMany(
        dto: PaginatorFactory<{}>,
        callback?: (isValid?: boolean, message?: string) => void,
        ignoreState = false,
        // boolean: filter to a single population (legacy two-tab behavior).
        // undefined: return both populations — used by the redesigned unified
        // POM Users tab. Backend treats a missing flag as "no filter".
        // Default must stay undefined: callers that pass `triToShowActiveUsers('all')`
        // pass `undefined` explicitly, which would otherwise fall back to the
        // default and silently filter to active users.
        showActiveUsers: boolean | undefined = undefined,
    ): Promise<PaginationFactory<UserTypes.Model> | void> {
        const {
            dispatch: { toggleLoading, setPagination },
        } = UserService;

        try {
            if (!ignoreState) toggleLoading('fetchMany', true);

            const qsfy = qs({
                perPage: dto.perPage,
                page: dto?.page,
                ...(showActiveUsers === undefined
                    ? {}
                    : { showActiveUsers: String(showActiveUsers) }),
                filter: {
                    ...dto?.filter,
                },
                sort: {
                    ...dto.sort,
                }
            });

            const {
                data: {
                    result: { items, page, perPage, total },
                    isValid,
                    message,
                },
            } = await api.get<
                DefaultRes<PaginationFactory<UserTypes.Model>>,
                AxiosResponse<DefaultRes<PaginationFactory<UserTypes.Model>>>
            >(`/users?${qsfy}`);

            if (!ignoreState) {
                toggleLoading('fetchMany', false);

                if (isValid) {
                    setPagination({ items, page, perPage, total });
                }
            }

            callback && callback(isValid, RESPONSE_CODES[message]);

            return { items, page, perPage, total };
        } catch (error: any) {
            toggleLoading('fetchMany', false);
            if (error.response) {
                const ERROR_CODE = (
                    error as AxiosError<DefaultRes<UserTypes.Model>>
                ).response?.data?.message;

                callback && callback(false, RESPONSE_CODES[ERROR_CODE!]);
            }
            callback && callback(false, String(error));
        }
    }

    // Deprovisioning report: soft-deleted users + their delete-time
    // deprovisionSnapshot. Stateless on purpose (no redux) — only the
    // report page consumes it.
    public static async fetchDeprovisioned(
        dto: PaginatorFactory<{}>,
    ): Promise<PaginationFactory<UserTypes.Model>> {
        const qsfy = qs({
            perPage: dto.perPage,
            page: dto?.page,
            filter: { ...dto?.filter },
            sort: { ...dto?.sort },
        });

        const {
            data: {
                result: { items, page, perPage, total },
            },
        } = await api.get<
            DefaultRes<PaginationFactory<UserTypes.Model>>,
            AxiosResponse<DefaultRes<PaginationFactory<UserTypes.Model>>>
        >(`/users/deprovisioned?${qsfy}`);

        return { items, page, perPage, total };
    }

    // Get ALL users in a subscription (and set it in the store)
    public static async fetchAll(
        callback?: (isValid?: boolean, message?: string) => void,
    ): Promise<UserTypes.Model[] | void> {
        const {
            dispatch: { toggleLoading, setAllUsers },
        } = UserService

        toggleLoading('fetchAll', true);

        try {
            const {
                data: { result, isValid, message },
            } = await api.get<
                DefaultRes<UserTypes.Model[]>,
                AxiosResponse<DefaultRes<UserTypes.Model[]>>
            >(`users/getSubscriptionUsers`);
            toggleLoading('fetchAll', false);

            if (isValid) {
                setAllUsers(result);
            }
            callback && callback(isValid, RESPONSE_CODES[message]);
            return result;
        } catch (error: any) {
            toggleLoading('fetchAll', false);

            if (error.response) {
                const ERROR_CODE = (
                    error as AxiosError<DefaultRes<UserTypes.Model[]>>
                ).response?.data?.message;

                callback && callback(false, RESPONSE_CODES[ERROR_CODE!]);
            }

            callback && callback(false, String(error));
        }
    }
}
